/**
 * Home: your people right now. Who is watching, who needs a word, the next live, your rooms, and how people move
 * through them over one period. A brand-new account gets the getting-started Home instead. A computer draws the page
 * below; a phone and an iPad draw SeasonHome from the same data.
 */
import { useState } from "react";
import { Link } from "react-router";
import { noteAboutOf } from "../../../shared/people-notes";
import type { HomeNeed, HomeRange } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { InviteSheet, inviteTargetOf } from "../../components/InviteSheet";
import { useLayout } from "../../components/season/layout";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import { Seg } from "../../components/ui/Seg";
import { useToast } from "../../components/ui/Toast";
import { api } from "../../lib/api";
import { warm } from "../../lib/kept";
import { fmt, longDay } from "../../lib/when";
import { NoteSheet } from "../people/sheets";
import { GettingStarted } from "./GettingStarted";
import { FollowUps, FunnelCard, HelloLine, NeedsCard, NextLiveCard, RightNow, RoomsCard, WhoCard } from "./parts";
import { SeasonHome } from "./SeasonHome";
import { greeting, RANGE_KEY, savedRange, useHome, useHomeDone } from "./useHome";
import "../../styles/home.css";

const RANGES: Array<{ value: HomeRange; label: string }> = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "all", label: "All time" },
];

export function prefetchHome(): void {
  warm(`home:${savedRange()}`, () => api.home(savedRange()));
}

function Hello({ title, watching, needs }: { title: string; watching?: number; needs?: number }) {
  return (
    <div className="hello">
      <div>
        <h1>{title}</h1>
        {watching === undefined || needs === undefined ? null : (
          <p>
            <HelloLine watching={watching} needs={needs} />
          </p>
        )}
      </div>
      <div className="small muted">{longDay(Date.now() / 1000)}</div>
    </div>
  );
}

/** Home: the computer's page on a computer, the Season page on a phone and an iPad. */
export function HomeView() {
  const layout = useLayout();
  return layout === "computer" ? <ComputerHome /> : <SeasonHome layout={layout} />;
}

function ComputerHome() {
  const { user } = useAuth();
  const [range, setRangeState] = useState<HomeRange>(savedRange);
  const { data, failed, reload, setData } = useHome(range);
  const done = useHomeDone({ setData, reload });
  const [note, setNote] = useState<HomeNeed | null>(null);
  const [inviting, setInviting] = useState(false);
  const toast = useToast();

  const setRange = (r: HomeRange) => {
    setRangeState(r);
    try {
      localStorage.setItem(RANGE_KEY, r);
    } catch {
      // a private window keeps the period for this visit only
    }
  };
  const name = data?.greeting_name ?? user?.first_name ?? "";
  const hello = greeting(new Date().getHours());
  const title = name ? `${hello}, ${name}` : hello;

  if (!data) {
    return (
      <div className="page">
        <Hello title={title} />
        {failed ? (
          <section className="card pad">
            <div className="row g12">
              <span className="sp">Home didn't load.</span>
              <Button size="s" onClick={reload}>
                Try again
              </Button>
            </div>
          </section>
        ) : (
          <section className="card pad" aria-busy="true">
            <div className="small muted">Loading</div>
          </section>
        )}
      </div>
    );
  }

  if (data.fresh) {
    return (
      <div className="page">
        <Hello title={title} />
        <GettingStarted data={data} />
      </div>
    );
  }

  const live = data.next_live;
  return (
    <div className="page">
      <Hello title={title} watching={data.right_now.watching.count} needs={data.needs.length} />
      {!data.setup.email ? (
        <div className="strip">
          <div className="ic">
            <Icon name="mail" />
          </div>
          <div className="sp">
            <b>Your emails are off.</b>{" "}
            {data.emails_held ? <span className="ink2">{`${fmt(data.emails_held)} ${data.emails_held === 1 ? "email" : "emails"} waiting.`}</span> : null}
          </div>
          <Link className="btn s p" to="/setup/email">
            Set up email
          </Link>
        </div>
      ) : null}
      <RightNow now={data.right_now} at={Date.now()} />
      <div className="grid g12 mt24" style={{ alignItems: "start" }}>
        <div className="s7">
          <NeedsCard needs={data.needs} faults={data.faults} onDone={done} onNote={setNote} />
        </div>
        <div className="s5 col" style={{ gap: 20 }}>
          {live ? <NextLiveCard live={live} onInvite={() => setInviting(true)} /> : null}
          {data.rooms.length ? <RoomsCard rooms={data.rooms} /> : null}
        </div>
      </div>
      <section className="results mt24" aria-label="Results">
        <div className="results-h">
          <h2>How people move through your rooms</h2>
          <Seg options={RANGES} value={range} onChange={setRange} label="Period" />
        </div>
        <div className="grid g12 results-g">
          <div className="s4">
            <WhoCard who={data.who} newOnly={range === "7d"} />
          </div>
          <div className="s8">
            <FunnelCard chain={data.chain} range={data.range} />
          </div>
        </div>
      </section>
      <FollowUps />
      <NoteSheet
        person={note ? note.person : null}
        about={noteAboutOf(note?.kind === "clicked" ? "nosign" : note?.kind)}
        onClose={() => setNote(null)}
        onSent={() => {
          if (note) void done(note.key, note.at);
        }}
      />
      {inviting && live ? (
        <InviteSheet
          webinar={inviteTargetOf({ id: live.webinar_id, title: live.title, live: { startsAt: live.starts_at, timezone: live.timezone, lengthMin: live.length_min } })}
          onClose={() => setInviting(false)}
          onSent={() => setInviting(false)}
          onToast={(m) => toast(m)}
        />
      ) : null}
    </div>
  );
}
